import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { Column } from '@repo/ui/data-table'
import { MoleculeIcon, PlusIcon, UploadIcon } from '@repo/ui/icons'
import React, { useState } from 'react'

import { API } from '../../../api/apis.config'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import FormulaFilter from '../../../components/pages/journey-places/formula/journey-place-formula-filter'
import { Paths } from '../../../lib/constants/path'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { Formula, FormulaFilters } from '../../../lib/types/models/formula/formula'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { t } from '../../../locale/utils'
import {
  transformFormulaFiltersToUrl,
  transformUrlToFormulaFilters
} from '../utils/formula-filter-mapping'

const JourneyPlacesFormulaWishListPage = () => {
  const tableRef = React.useRef<DynamicTableRef<Formula>>(null)
  const { downloadDocuments } = useDownloadDocuments()
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { modalState, toggleModal, closeModel } = useModalManager<'filters', Formula[]>([
    'filters'
  ])

  const { navigate, navigateWithEvent } = useNavigator()

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<FormulaFilters>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformFormulaFiltersToUrl,
      transformUrlToFilter: transformUrlToFormulaFilters
    })

  const columns: Column<Formula>[] = [
    {
      accessorKey: 'code',
      header: 'Code',
      enableSorting: true
    },
    {
      accessorKey: 'title',
      header: 'NOM de la formule ',
      enableSorting: true,
      cell: (data) => getTranslatedName(data.row.original)
    },
    {
      accessorKey: 'products[].country.name',
      header: 'Pays associés',
      enableSorting: false,
      cell: (data) => {
        const products = data.row.original.products
        return products?.length > 0
          ? products?.map((product) => product.country.name).join(', ')
          : '-'
      }
    },
    {
      accessorKey: 'description',
      header: 'Description fiche de dispo ME',
      enableSorting: true
    }
  ]

  return (
    <div>
      <UserNavBar
        title="Formules"
        icon={(props) => <MoleculeIcon {...props} fill={tailwindColors.secondary.DEFAULT} />}
      />
      <div className="h-4" />
      <DynamicDataTable<Formula, FormulaFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title="Toutes les formules"
        columns={columns}
        deleteMessage={{
          name: 'formules',
          attributeName: 'id',
          deleteOne: (formula) =>
            `Êtes-vous sûr de vouloir supprimer cette formule "${getTranslatedName(formula)}"?`,
          deleteMany: (formulas) =>
            t('dynamic.data.table.delete.many.label', {
              message: `formules "${formulas.map((formula) => getTranslatedName(formula)).join('", "')}"`
            })
        }}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        selectable
        allowSearchParam
        endpoint={API.formulas.getAll.url}
        onRowClick={(row, event) =>
          navigateWithEvent(
            Paths.FORMULA_WISH_LIST_UPDATE_PAGE.replace(':id', row.id.toString()),
            event
          )
        }
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Ajouter une nouvelle formule',
            fn: () => {
              navigate(Paths.FORMULA_WISH_LIST_CREATE_PAGE)
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter',
            fn: (rows) => {
              const ids = rows.map((row) => row.id)
              downloadDocuments({
                endpoint: API.formulas.exportExcel.url,
                ids
              })
            }
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <FormulaFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default JourneyPlacesFormulaWishListPage
